import Fuse from 'fuse.js'
import {
  node,
  bool,
  array,
  object,
  func,
  any,
  number,
  string,
} from 'prop-types'
import { PureComponent, Children, cloneElement } from 'react'
import { Tooltip } from '@opallabs/lapidary'
import Menu from '../../Menu'
import { Popover } from '../../PopoverV2'
import { kuiperClassNames, stopEvent } from '../../utils'
import { trackDisconnected, disconnectedChild } from '../WithOutsideTracking'
import './styles.scss'

function filtered(options, term, settings) {
  if (options && term) {
    const fuse = new Fuse(options, settings)
    return fuse.search(term).map((result) => result.item)
  }
  return options
}

const searchOptions = {
  keys: ['title', 'fullName'],
  threshold: 0.3,
}

const defaultSelectedComparator = (option, value) => {
  if (Array.isArray(value)) {
    return Boolean(value.find((candidate) => candidate.id === option.id))
  }

  return option.id === value.id
}

export default class WithMenuComponent extends PureComponent {
  static propTypes = {
    avatared: bool,
    canSelectAll: bool,
    children: node,
    clearSearchOnSelect: bool,
    closeOnSelect: bool,
    disabled: bool,
    focused: bool,
    isMultiSelect: bool,
    menuClassName: string,
    menuContent: node,
    noSuggestions: bool,
    onChange: func,
    onClearAll: func,
    onRemove: func,
    onSelect: func,
    onSuggest: func,
    optionDecorator: func,
    options: array,
    optionsType: string,
    overscanCount: number,
    popoverOptions: object,
    rowHeight: number,
    searchable: bool,
    searchOptions: object,
    selectMatchOnEnter: bool,
    selectedComparator: func,
    showDeselectIcon: bool,
    showSelected: bool,
    tooltip: object,
    value: any,
  }

  static defaultProps = {
    searchOptions,
    showSelected: true,
    selectedComparator: defaultSelectedComparator,
  }

  constructor(props) {
    super(props)

    this.state = {
      focused: props.focused,
      term: '',
      tabbed: false,
    }

    this.onBlur = this.onBlur.bind(this)
    this.onClick = this.onClick.bind(this)
    this.onFocus = this.onFocus.bind(this)
    this.onInputBlur = this.onInputBlur.bind(this)
    this.onKeyDown = this.onKeyDown.bind(this)
    this.onKeyUp = this.onKeyUp.bind(this)
    this.onSelect = this.onSelect.bind(this)
    this.onSuggest = this.onSuggest.bind(this)
    this.trackElement = this.trackElement.bind(this)
    this.trackMenu = this.trackMenu.bind(this)
  }

  componentDidMount() {
    this.mounted = true
  }

  componentWillUnmount() {
    this.mounted = false
  }

  onBlur(e) {
    if (e) {
      e.stopPropagation()
    }

    if (e && e.relatedTarget && disconnectedChild(this, e.relatedTarget)) {
      return
    }

    this.setState({
      focused: false,
      tabbed: false,
    })
  }

  onClick(e) {
    if (e) {
      e.stopPropagation()
    }

    if (this.props.noSuggestions) {
      return
    }
    this.setState({
      focused: !this.props.disabled,
    })
  }

  onFocus(e) {
    if (e) {
      e.stopPropagation()
    }
    this.setState({
      tabbed: true,
    })
  }

  onSuggest(term) {
    if (this.props.onSuggest) {
      this.props.onSuggest(term)
    }
    this.setState({
      term,
    })
  }

  onInputBlur(e) {
    if (e) {
      e.stopPropagation()
    }

    if (e?.relatedTarget && disconnectedChild(this, e.relatedTarget)) {
      return
    }

    this.setState({
      tabbed: false,
      focused: false,
    })
  }

  onSelect(...options) {
    const {
      clearSearchOnSelect,
      closeOnSelect,
      isMultiSelect,
      onRemove,
      onSelect,
      onChange,
      selectMatchOnEnter,
    } = this.props
    // MultiSelect passes the typed text on Enter.
    if (isMultiSelect && selectMatchOnEnter && typeof options[0] === 'string') {
      const match = this.optionMatching(options[0])
      // Enter only adds. It never removes a selected option.
      if (match && !this.selected(match)) {
        this.onSelect(match)
      }
      return true
    }
    if (!isMultiSelect) {
      const option = options[0]
      if (option) {
        if (onSelect && !this.selected(option)) {
          onSelect(option)
        }
        if (onRemove && this.selected(option)) {
          onRemove(option)
        }
        if (onChange) {
          onChange(option)
        }
        if (closeOnSelect) {
          this.onBlur()
        }
      }
      return true
    }
    const { toSelect, toRemove } = options.reduce(
      (accum, option) => {
        const selected = this.selected(option)
        if (selected) {
          return {
            ...accum,
            toRemove: accum.toRemove.concat(option),
          }
        }
        return {
          ...accum,
          toSelect: accum.toSelect.concat(option),
        }
      },
      {
        toSelect: [],
        toRemove: [],
      }
    )
    if (onSelect && toSelect.length) {
      onSelect(...toSelect)
    }
    if (onRemove && toRemove.length) {
      onRemove(...toRemove)
    }
    if (onChange && (toSelect.length || toRemove.length)) {
      onChange(...toSelect.concat(toRemove))
    }
    if (clearSearchOnSelect && (toSelect.length || toRemove.length)) {
      this.onSuggest('')
    }
    if (closeOnSelect && (toSelect.length || toRemove.length)) {
      this.onBlur()
    }
    return true
  }

  trackElement(trackedElement) {
    if (trackedElement) {
      this.trackedElement = trackedElement
    }
  }

  get attachedComponent() {
    const { children } = this.props
    const { focused, tabbed, term } = this.state
    const {
      onBlur,
      onClick,
      onFocus,
      onInputBlur,
      onSelect,
      onSuggest,
      trackElement,
      onKeyUp,
      onKeyDown,
      value,
    } = this
    const child = Children.only(children)
    return cloneElement(child, {
      tabbed,
      focused,
      hasMenu: true,
      onBlur,
      onClick,
      onFocus,
      onInputBlur,
      onKeyUp,
      onKeyDown,
      onSelect,
      onSuggest,
      interactionHandlers: {
        onBlur,
        onFocus,
        onKeyUp,
        onKeyDown,
        onClick,
        tabIndex: 0,
      },
      term,
      trackElement,
      value,
    })
  }

  get open() {
    const { disabled } = this.props
    const { focused } = this.state
    return !disabled && focused
  }

  trackMenu(element) {
    if (element) {
      this.trackedMenuElement = element
    }
  }

  get value() {
    return this.props.value || (this.props.isMultiSelect ? undefined : [])
  }

  selected(option) {
    const { selectedComparator } = this.props
    if (this.props.isMultiSelect) {
      return selectedComparator(option, this.value)
    }
    return option === this.value
  }

  get filteredOptions() {
    if (this.state.term.length > 0) {
      return filtered(
        this.props.options,
        this.state.term,
        this.props.searchOptions
      )
    }
    return this.props.options
  }

  // Menu shows selected options even when showSelected is false, so count them.
  optionMatching(term) {
    const visible = this.filteredOptions
    const lowerTerm = term.trim().toLowerCase()
    const exact = visible.find(
      (option) => option.title?.toLowerCase() === lowerTerm
    )
    if (exact) {
      return exact
    }
    return visible.length === 1 ? visible[0] : undefined
  }

  get menu() {
    const {
      avatared,
      canSelectAll,
      menuClassName,
      menuContent,
      isMultiSelect,
      onClearAll,
      optionDecorator,
      optionsType,
      overscanCount,
      rowHeight,
      showDeselectIcon,
      searchable,
      selectedComparator,
      showSelected,
    } = this.props
    const contents = menuContent || (
      <Menu
        avatared={avatared}
        canSelectAll={canSelectAll}
        className={menuClassName}
        dropdown={true}
        isMultiSelect={isMultiSelect}
        onClearAll={onClearAll}
        onSelect={this.onSelect}
        optionDecorator={optionDecorator}
        options={this.filteredOptions}
        optionsType={optionsType}
        overscanCount={overscanCount}
        rowHeight={rowHeight}
        searchable={searchable}
        showDeselectIcon={showDeselectIcon}
        selectedComparator={selectedComparator}
        showSelected={showSelected}
        value={this.value}
      />
    )
    return (
      <div
        className={kuiperClassNames('attached-menu')}
        ref={trackDisconnected.bind(this)}
      >
        {contents}
      </div>
    )
  }

  upArrowPressed() {
    if (this.trackedMenuElement) {
      this.trackedMenuElement.upArrowPressed()
    }
  }

  downArrowPressed() {
    if (this.trackedMenuElement) {
      this.trackedMenuElement.downArrowPressed()
    }
  }

  enterPressed() {
    if (this.trackedMenuElement) {
      this.trackedMenuElement.enterPressed()
    }
    const { tabbed } = this.state
    if (tabbed) {
      this.setState({
        focused: true,
        tabbed: false,
      })
    }
  }

  escapePressed(e) {
    if (this.state.focused) {
      stopEvent(e)
      this.onBlur()
    }
  }

  onKeyUp(e) {
    switch (e.keyCode) {
      case 38:
        this.upArrowPressed(e)
        break
      case 40:
        this.downArrowPressed(e)
        break
      case 13:
        this.enterPressed()
        break
      case 27:
        this.escapePressed(e)
        break
      default:
        break
    }
  }

  onKeyDown(e) {
    switch (e.keyCode) {
      case 9:
        this.onBlur()
        break
      default:
        break
    }
  }

  popoverOptions() {
    return this.props.popoverOptions
  }

  render() {
    const { attachedComponent, menu, open } = this

    const { noSuggestions } = this.props

    const tooltipContent =
      typeof this.props.tooltip === 'string'
        ? this.props.tooltip
        : (this.props.tooltip?.content ?? '')

    const component = tooltipContent ? (
      <Tooltip disabled={false} text={tooltipContent} theme="light">
        {attachedComponent}
      </Tooltip>
    ) : (
      attachedComponent
    )

    if (noSuggestions) {
      return component
    }
    return (
      <Popover
        {...this.popoverOptions()}
        open={open}
        content={menu}
        trigger={component}
      />
    )
  }
}
